import { cloneDeep } from "lodash";
import moment from "moment";
import i18nService from "@/services/i18n";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { reportDkiRExpensesClassificators, reportDkiRFilterOptions, reportDkiRIndicators, reportDkiRMonths, reportDkiRSegmentations } from "../report-dki-r";
import { ReportDkiRFilterSettingsForm } from "../settings-form/ReporDkiRFilterSettingsForm";
import { ReportDkiRFilterSettings } from "../settings/ReportDkiRFilterSettings";
import { ReportDkiRTableDataRequest } from "../data-request/ReportDkiRTableDataRequest";
import { ReportDkiRJournalRequest } from "../journal/ReportDkiRJournalRequest";
import { ReportDkiRRow } from "../table-data/ReportDkiRRow";
import { mapDkiRRow, ReportDkiRRawResponse } from "../table-data/mapDkiRRow";
import { Indicator } from "../../../common/types/Indicator";

export class ReportDkiRFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(reportDkiRFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        // 1. Валидаций
        if (settings.reportName !== 'DKI-R') {
            throw new Error('error_type_filter_settings')
        }
        if (options.key !== 'DKI-R') {
            throw new Error('error_type_filter_options')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('error_not_found_report_name')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)!
        if (!reportType) {
            throw new Error('error_not_found_report_type')
        }
        reportType = { ...reportType }

        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('error_not_found_class_type')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('error_not_found_data_source')
        }
        dataSource = { ...dataSource }

        let periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('error_not_found_periodicity')
        }
        periodicity = { ...periodicity }

        let regionOption = options.regions.find(it => it.value === settings.region)
        if (!regionOption) {
            throw new Error('error_not_found_region_option')
        }
        regionOption = { ...regionOption }

        const budgetTypeOptions = cloneDeep(
            options.budgetTypes.filter(it => settings.budgetTypes.includes(it.value))
        )

        let measureUnitOption = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnitOption) {
            throw new Error('error_not_found_measure_unit')
        }
        measureUnitOption = { ...measureUnitOption }

        let paymentTypeOption = options.paymentTypes.find(it => it.value === settings.paymentType)
        if (!paymentTypeOption) {
            throw new Error('error_not_found_payment_type')
        }
        paymentTypeOption = { ...paymentTypeOption }

        let mutuallyRedeemingOption = options.mutuallyRedeeming.find(it => it.value === settings.mutuallyRedeeming)
        if (!mutuallyRedeemingOption) {
            throw new Error('error_not_found_mutually_redeeming_option')
        }
        mutuallyRedeemingOption = { ...mutuallyRedeemingOption }

        const mutuallyRedeemingLevelOptions = cloneDeep(
            options.mutuallyRedeemingLevels.filter(it => settings.mutuallyRedeemingLevels.includes(it.value))
        )

        const segmentations = settings.segmentations ?? cloneDeep(reportDkiRSegmentations)

        let isRoundUp: boolean = false
        if (settings.roundUpTo != null) {
            isRoundUp = true
        }

        const developTypeOption = cloneDeep(options.developTypes.find(it => it.value === settings.developType) ?? null)

        const indicators = cloneDeep(settings.indicators ?? reportDkiRIndicators)
        const months = monthsToForm(settings.months)

        // 2. Формирование
        const form: ReportDkiRFilterSettingsForm = {
            // Base:
            key: 'DKI-R',
            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            periodicity: periodicity,
            // Custom:
            region: regionOption,
            date: new Date(settings.date),
            budgetTypes: budgetTypeOptions,
            measureUnit: measureUnitOption,
            roundUp: isRoundUp,
            roundUpTo: settings.roundUpTo,
            expenses: cloneDeep(settings.expenses),
            paymentType: paymentTypeOption,
            mutuallyRedeeming: mutuallyRedeemingOption,
            mutuallyRedeemingLevels: mutuallyRedeemingLevelOptions,
            segmentations: segmentations,
            isTree: settings.isTree,
            expenseTotal: settings.expenseTotal,
            developType: developTypeOption,
            indicators: indicators,
            months: months
        }

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'DKI-R') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (form.region == null) {
            throw new Error('error_input_region')
        }
        if (!form.date) {
            throw new Error('error_input_date')
        }

        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }

        if (!form.mutuallyRedeeming) {
            throw new Error('error_input_mutually_redeeming')
        }

        if (form.developType == null) {
            throw new Error('error_input_develop_type')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const strDate = moment(form.date).format('YYYY-MM-DD')

        const settings: ReportDkiRFilterSettings = {
            // Base:
            reportName: 'DKI-R',
            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,
            // Custom:
            region: form.region.value,
            date: strDate,
            budgetTypes: form.budgetTypes.map(it => it.value),
            measureUnit: form.measureUnit.value,
            roundUpTo: roundUpTo,
            expenses: cloneDeep(form.expenses),
            paymentType: form.paymentType.value,
            mutuallyRedeeming: form.mutuallyRedeeming.value,
            mutuallyRedeemingLevels: form.mutuallyRedeemingLevels.map(it => it.value),
            segmentations: cloneDeep(form.segmentations ?? reportDkiRSegmentations),
            isTree: form.isTree,
            expenseTotal: form.expenseTotal,
            developType: form.developType.value ?? null,
            indicators: cloneDeep(form.indicators),
            months: monthsToSettings(form.months)
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(reportDkiRFilterOptions)

        // Дата — последний день предыдущего месяца (RQ.eAk.RcDkiR.23)
        const lastDayOfPreviousMonth = moment().subtract(1, 'months').endOf('month').toDate()

        const emptyForm: ReportDkiRFilterSettingsForm = {
            key: 'DKI-R',
            // Base fields
            reportName: options.reportNames.find(it => it.value === 'DKI-R')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'EXPENSES')!,
            dataSource: options.dataSources.find(it => it.value === 'IISK')!,
            periodicity: options.periodicities.find(it => it.value === 'DAY')!,
            // Custom fields
            region: null,
            date: lastDayOfPreviousMonth,
            budgetTypes: [],
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            roundUp: false,
            roundUpTo: null,
            expenses: cloneDeep(reportDkiRExpensesClassificators),
            paymentType: options.paymentTypes.find(it => it.value === 'PAYMENTS')!,
            mutuallyRedeeming: null, // Заполняется по региону (RQ.eAk.RcDkiR.14) в onRegionInput
            mutuallyRedeemingLevels: cloneDeep(options.mutuallyRedeemingLevels),
            segmentations: cloneDeep(reportDkiRSegmentations),
            isTree: true,
            expenseTotal: false,
            developType: options.developTypes.find(it => it.value === null)!,
            indicators: cloneDeep(reportDkiRIndicators),
            months: cloneDeep(reportDkiRMonths)
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидаций и Формирование данных для таблицы
        if (form.key !== 'DKI-R') {
            throw new Error('error_type_filter_settingsForm')
        }

        const date = form.date
        if (!date) {
            throw new Error('error_input_date')
        }

        const regionOption = form.region
        if (!regionOption) {
            throw new Error('error_input_region')
        }
        const region = regionOption.value

        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }
        if (!form.mutuallyRedeeming) {
            throw new Error('error_input_mutually_redeeming')
        }
        if (form.developType == null) {
            throw new Error('error_input_develop_type')
        }
        if (!form.segmentations?.filter(it => it.active).length) {
            throw new Error('error_input_segmentation')
        }

        const strDate = moment(date).format('YYYY-MM-DD')

        // Активные разрезы расходов (RQ.eAk.RcDkiR.25) — только для валидации минимума;
        // сам состав/порядок отправляется бэкенду как есть в `expenses` (RQ.eAk.RcDkiR.11)
        if (form.expenses.filter(it => it.active).length === 0) {
            throw new Error('error_input_at_least_one_kbk')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        // Месяцы, выбранные для отображения (RQ.eAk.RcDkiR.20). ВАЖНО: бэкенд сам фильтрует
        // ответ по этому списку (не только Excel/журнал) — поэтому список активных месяцев
        // обязателен и в основном запросе данных, а не только в journalParams.
        const activeMonths: number[] = form.months.filter(it => it.active).map(it => Number(it.key))

        const params: ReportDkiRTableDataRequest = {
            user_id: userId,
            date: strDate,
            region: region,
            budgetTypes: form.budgetTypes.map(it => it.value),
            paymentType: form.paymentType.value,
            mutuallyRedeeming: form.mutuallyRedeeming.value,
            mutuallyRedeemingLevels: form.mutuallyRedeemingLevels.map(it => it.value),
            segmentations: cloneDeep(form.segmentations ?? reportDkiRSegmentations),
            measureUnit: form.measureUnit.value,
            roundUpTo: roundUpTo,
            isTree: form.isTree,
            expenseTotal: form.expenseTotal,
            developType: form.developType.value,
            indicators: cloneDeep(form.indicators),
            months: activeMonths,
            expenses: cloneDeep(form.expenses),
            locale: i18nService.locale,
        }

        const response = await fetch('/api-py/monitoring/reports-constructor/dki-r', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params)
        })
        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }
        const raw: ReportDkiRRawResponse = await response.json()
        const tableData: ReportDkiRRow[] = raw.rows.map(
            (rawRow, index) => mapDkiRRow(rawRow, raw.indicators, raw.months, index)
        )

        // 2. Формирование параметров для выгрузки журнал.
        // DkiRExcelRequest на бэкенде наследует DkiRRequestData (Pydantic) — плоская модель,
        // без отдельного вложенного "params" (см. ReportDkiRJournalRequest.ts).
        const settings: FilterSettings = this.formToSettings(cloneDeep(form))

        const journalParams: ReportDkiRJournalRequest = {
            reportName: "DKI-R", // Приходится задавать для поддержки generics
            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            date: strDate,
            region: region,
            budgetTypes: form.budgetTypes.map(it => it.value),
            paymentType: form.paymentType.value,
            mutuallyRedeeming: form.mutuallyRedeeming.value,
            mutuallyRedeemingLevels: form.mutuallyRedeemingLevels.map(it => it.value),
            segmentations: cloneDeep(form.segmentations ?? reportDkiRSegmentations),
            measureUnit: form.measureUnit.value,
            roundUpTo: roundUpTo,
            isTree: form.isTree,
            expenseTotal: form.expenseTotal,
            developType: form.developType.value,
            indicators: cloneDeep(form.indicators),
            months: activeMonths,
            expenses: cloneDeep(form.expenses),
            locale: i18nService.locale,
        }

        // Итоговая строка «ИТОГО» приходит с бэкенда первой строкой готовой (RQ.eAk.RcDkiR.28) —
        // на фронте не пересчитывается.
        // cashDateWarning (RQ.58/RQ.59, §5.4) — предупреждение об отсутствии среза кассового
        // исполнения на отчётную дату; monthsWithoutSnapshot намеренно не прокидывается дальше —
        // чисто информационное поле, toast-предупреждение строится только из cashDateWarning
        // (см. filters-form2.vue::cashDateWarningToastText).
        const result: TableDataAndJournalParams = {
            tableData: cloneDeep(tableData),
            journalParams: journalParams,
            cashDateWarning: raw.cashDateWarning ?? null
        }

        return result
    }
}

/** settings.months (число[]) -> форма (Indicator[] на все 12 месяцев) */
const monthsToForm = (months: number[] | undefined): Indicator[] => {
    const activeMonths = months ?? reportDkiRMonths.map(it => Number(it.key))
    return reportDkiRMonths.map(it => ({
        key: it.key,
        active: activeMonths.includes(Number(it.key))
    }))
}

/** форма (Indicator[]) -> settings.months (число[]) */
const monthsToSettings = (months: Indicator[]): number[] => {
    return months.filter(it => it.active).map(it => Number(it.key))
}
